import create from "zustand";
import { devtools, persist } from "zustand/middleware";
import { ItemI } from "../shared";
import { getShelfWithNewItem } from "./actions/items/getShelfWithNewItem";
import {
    getCopySection,
    getNewSection,
    getRemoveSection,
    getSectionConnectorYPos,
    getSectionItemsYPos,
    getUpdateNomenkature,
    getUpdateSections,
    updateItemsColors,
} from "./actions/sections";
import {
    BeamInterface,
    ColorsItemsInterface,
    SectionInterface,
    SectionNomenklature,
    SideTypeWithoutTop,
    Sides,
    StoreWallInterface,
} from "./types/store";
import { updateBeams } from "./actions/sections/updateBeams";
import produce from "immer";
import { updateBrackets } from "./actions/items/updateBrackets";
import { updateItem } from "./actions/items/updateItem";
import { removeSpiritItems } from "./actions/items/removeSpiritItems";
import { moveItem } from "./actions/items/moveItem";
import { SpiritMap } from "./helpers/getItemFillingRangesNew";
import { addSpiritItems } from "./actions/items/addSpiritItems";
import { updateShelfRodIds } from "./helpers/updateShelfRodIds";
import { updateRodBitrixIds } from "./helpers/updateRodBitrixIds";
import { useStoreSystem } from "./useStoreSystem";
import { Nomenklature } from "../classes/Nomenklature";
import { isNewSite } from "../utils";
interface PropertyShelfInterface {
    wallName: SideTypeWithoutTop;
    property: PropertyInterface;
    value: number;
}

export type ShelvingInterface = {
    [key in Sides]: StoreWallInterface;
};

export interface ShelvingStoreInterface {
    colorsItems: ColorsItemsInterface;
    shelving: ShelvingInterface;

    setPropertyShelving: (propertyArr: PropertyShelfInterface[]) => void;

    addSectionInShelving: (width: number, isIndent: boolean) => void;
    getCurrentShelving: () => ReturnType<() => StoreWallInterface>;
    updateSection: (newSection: SectionInterface) => void;
    copySection: (section: SectionInterface) => void;
    removeSection: (removeSection: SectionInterface) => void;
    addItemToSection: (newItem: ItemI, editSectionID: string, newY?: number) => void;
    updateConnectorYPositions: (connectorYPositions: number[], sectionId: string, isFirstRack?: boolean) => void;
    setSectionItemsYPos: (id: string | null, sectionId: string) => void;
    setColorsItems: (colorsItems: ColorsItemsInterface) => void;
    updateBeams: (side: SideTypeWithoutTop) => void;
    updateNomenklature: (side: SideTypeWithoutTop) => void;
    updateItemsYPositionsAfterResizeRoomHeight: (deltaHeight: number, Side: SideTypeWithoutTop | "all") => void;
    updateBrackets: (itemId: string, sectionId: string, onDragStartPositionY?: number) => void;
    updateItem: (id: string, sectionID: string) => void;
    removeSpiritItems: (itemId?: string) => void;
    moveItem: (id: string, position: number[]) => void;
    addSpiritItems: (spiritsMap: SpiritMap[], item: ItemI) => void;
    resetShelvingStore: (withFilling: boolean, withRoom: boolean) => void;
    deleteCurrentEditItem: () => void;
    loadProject: (project: any) => void;
    removeItem: (itemId: string) => void;
}

type PropertyInterface = "height" | "length" | "offsetCeiling" | "sideIndent";

type ShelvingSizes = {
    height: number;
    length: number;
    offsetCeiling: number;
    sideIndent: number;
};

const INITIAL_SHELVING_SIZES: ShelvingSizes = {
    height: 2400,
    length: 4800,
    offsetCeiling: 400,
    sideIndent: 40,
};

export const useShelvingStore = create<
    ShelvingStoreInterface,
    [["zustand/devtools", never], ["zustand/persist", ShelvingStoreInterface]]
>(
    devtools(
        persist(
            (set, get) => ({
                colorsItems: {
                    guides: "white",
                    shelves: "white",
                    perfpanels: "white",
                    brackets: "red",
                    tireRacks: "red",
                    workbenchToolbox: "orange",
                } as ColorsItemsInterface,
                shelving: {
                    [Sides.FRONT]: {
                        name: Sides.FRONT,
                        ...INITIAL_SHELVING_SIZES,
                        beams: [] as BeamInterface[],
                        sections: [] as SectionInterface[],
                        firstRackHeight: null,
                        itemsYPos: null,
                        connectorYPositions: {},
                        price: {
                            current: 0,
                            old: 0,
                        },
                        nomenklature: new Nomenklature(),
                        sectionsNomenklature: [] as SectionNomenklature[],
                    },
                    [Sides.LEFT]: {
                        name: Sides.LEFT,
                        ...INITIAL_SHELVING_SIZES,
                        beams: [],
                        sections: [],
                        firstRackHeight: null,
                        itemsYPos: null,
                        connectorYPositions: {},
                        price: {
                            current: 0,
                            old: 0,
                        },
                        nomenklature: new Nomenklature(),
                        sectionsNomenklature: [] as SectionNomenklature[],
                    },
                    [Sides.RIGHT]: {
                        name: Sides.RIGHT,
                        ...INITIAL_SHELVING_SIZES,
                        beams: [],
                        sections: [],
                        firstRackHeight: null,
                        itemsYPos: null,
                        connectorYPositions: {},
                        price: {
                            current: 0,
                            old: 0,
                        },
                        nomenklature: new Nomenklature(),
                        sectionsNomenklature: [] as SectionNomenklature[],
                    },
                    [Sides.BACK]: {
                        name: Sides.BACK,
                        ...INITIAL_SHELVING_SIZES,
                        beams: [],
                        sections: [],
                        firstRackHeight: null,
                        itemsYPos: null,
                        connectorYPositions: {},
                        price: {
                            current: 0,
                            old: 0,
                        },
                        nomenklature: new Nomenklature(),
                        sectionsNomenklature: [] as SectionNomenklature[],
                    },
                } as ShelvingInterface,

                setPropertyShelving: (propertyArr) => {
                    const newShelving = structuredClone(get().shelving);
                    propertyArr.forEach(
                        (property) => (newShelving[property.wallName][property.property] = property.value)
                    );
                    set({ shelving: newShelving });
                },
                addSectionInShelving: (width, isIndent) => {
                    const currentSide = useStoreSystem.getState().currentSide;
                    if (currentSide !== "top") {
                        const newShelving = structuredClone(get().shelving);
                        newShelving[currentSide] = getNewSection({
                            width,
                            isIndent,
                            currentShelving: newShelving[currentSide],
                        });
                        set({ shelving: newShelving });
                    }
                },
                moveItem: (id, position) => {
                    const currentShelving = get().getCurrentShelving();
                    set(
                        produce((state) => {
                            state.shelving[currentShelving.name].sections = moveItem(id, position, currentShelving);
                        })
                    );
                },
                getCurrentShelving: () => {
                    const currentSide = useStoreSystem.getState().currentSide;
                    if (currentSide !== "top") {
                        return get().shelving[currentSide];
                    }
                    return get().shelving.front;
                },
                updateSection: (newSection) => {
                    const currentShelving = get().getCurrentShelving();
                    const newShelving = {
                        ...get().shelving,
                        [currentShelving.name]: {
                            ...currentShelving,
                            sections: getUpdateSections({ currentShelving, newSection }),
                        },
                    };
                    set({ shelving: newShelving });
                },
                updateItemsYPositionsAfterResizeRoomHeight: (deltaHeight, side) => {
                    if (deltaHeight === 0) {
                        return;
                    }
                    const updateSection = (section: SectionInterface) => {
                        const updatedItems = section.items.map((i) => {
                            if (Array.isArray(i.brackets.left.position) && Array.isArray(i.brackets.right.position)) {
                                return {
                                    ...i,
                                    position: [i.position[0], i.position[1] + deltaHeight, i.position[2]],
                                    brackets: {
                                        ...i.brackets,
                                        left: {
                                            ...i.brackets.left,
                                            position: [
                                                i.brackets.left.position[0],
                                                i.brackets.left.position[1] + deltaHeight,
                                                i.brackets.left.position[2],
                                            ],
                                        },
                                        right: {
                                            ...i.brackets.right,
                                            position: [
                                                i.brackets.right.position[0],
                                                i.brackets.right.position[1] + deltaHeight,
                                                i.brackets.right.position[2],
                                            ],
                                        },
                                    },
                                };
                            } else {
                                return {
                                    ...i,
                                    position: [i.position[0], i.position[1] + deltaHeight, i.position[2]],
                                };
                            }
                        });
                        return { ...section, items: updatedItems };
                    };
                    if (side !== "all") {
                        const shelving = get().shelving[side];
                        const updatedSections = shelving.sections.map(updateSection);
                        set({
                            shelving: {
                                ...get().shelving,
                                [side]: {
                                    ...get().shelving[side as Sides],
                                    sections: updatedSections,
                                },
                            },
                        });
                    } else {
                        const shelvings = get().shelving;
                        Object.entries(shelvings).forEach(([side, shelving]) => {
                            const updatedSections = shelving.sections.map(updateSection);
                            set({
                                shelving: {
                                    ...get().shelving,
                                    [side]: {
                                        ...get().shelving[side as Sides],
                                        sections: updatedSections,
                                    },
                                },
                            });
                        });
                    }
                },
                copySection: (section) => {
                    const currentShelving = get().getCurrentShelving();
                    let newSections = getCopySection({ currentShelving, section });
                    if (newSections.length === currentShelving.sections.length) {
                        return;
                    }
                    //Получаем новую скопированную секцию и обновляем у неё кронштейны для каждого элемента
                    const copySection = newSections[newSections.length - 1];
                    let i = 0;
                    do {
                        set(
                            produce((state) => {
                                state.shelving[currentShelving.name].sections = updateBrackets(
                                    copySection?.items[i]?.id || "",
                                    copySection.id,
                                    { ...currentShelving, sections: newSections }
                                );
                                newSections = state.shelving[currentShelving.name].sections;
                            })
                        );
                        i++;
                    } while (i < copySection.items.length);
                },
                removeSection: (removeSection) => {
                    const currentShelving = get().getCurrentShelving();
                    const newShelving = {
                        ...get().shelving,
                        [currentShelving.name]: {
                            ...currentShelving,
                            sections: getRemoveSection({ currentShelving, removeSection }),
                        },
                    };
                    set({ shelving: newShelving });
                },
                updateConnectorYPositions: (connectorYPositions, sectionId, isFirstRack) => {
                    const shelving = get().shelving;
                    const shelvingArr = Object.values(shelving);
                    let currentName = Sides.FRONT;
                    for (let q = 0; q < shelvingArr.length; q++) {
                        if (shelvingArr[q].sections.find((section: SectionInterface) => section.id === sectionId)) {
                            currentName = shelvingArr[q].name;
                            const currentShelving = shelving[currentName];
                            const newShelving = {
                                ...shelving,
                                [currentName]: {
                                    ...currentShelving,
                                    connectorYPositions: getSectionConnectorYPos({
                                        currentShelving,
                                        connectorYPositions,
                                        sectionId,
                                        isFirstRack,
                                    }),
                                },
                            };
                            set({ shelving: newShelving });
                            break;
                        }
                    }
                },
                setSectionItemsYPos: (id, sectionId) => {
                    const currentShelving = get().getCurrentShelving();
                    const newShelving = {
                        ...get().shelving,
                        [currentShelving.name]: {
                            ...currentShelving,
                            itemsYPos: getSectionItemsYPos({ currentShelving, id, sectionId }),
                        },
                    };
                    set({ shelving: newShelving });
                },
                addItemToSection: (newItem, editSectionID, newY) => {
                    const currentShelving = get().getCurrentShelving();
                    let updatedShelf = getShelfWithNewItem({ currentShelving, editSectionID, newItem, newY });

                    // Если добавляется штанга, пересчитываем ID штанг для цепочек
                    const isRodAdded = newItem.TYPE?.some((t) => t === "Штанга");
                    if (isRodAdded) {
                        console.log("Добавлена штанга, пересчитываем ID штанг в цепочках");
                        updatedShelf = { ...updatedShelf, sections: updateRodBitrixIds(updatedShelf.sections) };
                    }

                    const newShelving = {
                        ...get().shelving,
                        [currentShelving.name]: updatedShelf,
                    };

                    if (isNewSite()) {
                        const currentColorsItems = get().colorsItems;
                        const { shelving } = updateItemsColors(
                            currentColorsItems.brackets,
                            newShelving,
                            currentColorsItems.tireRacks ?? currentColorsItems.brackets
                        );
                        set({ shelving });
                    } else {
                        set({ shelving: newShelving });
                    }
                },
                setColorsItems: (colorsItems) => {
                    if (isNewSite()) {
                        // Пока только brackets красим, остальное цинковка по номенклатуре
                        const { shelving } = updateItemsColors(
                            colorsItems.brackets,
                            get().shelving,
                            colorsItems.tireRacks ?? colorsItems.brackets
                        );
                        set({ colorsItems, shelving });
                    } else {
                        set({ colorsItems });
                    }
                },
                updateBeams: (side) =>
                    set(
                        produce((state) => {
                            state.shelving[side].beams = updateBeams(get().shelving[side]);
                        })
                    ),
                updateNomenklature: (side) => {
                    const updateNomenkature = getUpdateNomenkature(get().shelving[side]);
                    set(
                        produce((state) => {
                            state.shelving[side].price.current = updateNomenkature.current;
                            state.shelving[side].price.old = updateNomenkature.old;
                            state.shelving[side].nomenklature = updateNomenkature.nomenklature;
                            state.shelving[side].sectionsNomenklature = updateNomenkature.sectionsNomenklature;
                        })
                    );
                },
                updateBrackets: (itemId, sectionId, onDragStartPositionY) => {
                    const currentShelving = get().getCurrentShelving();
                    set(
                        produce((state) => {
                            state.shelving[currentShelving.name].sections = updateBrackets(
                                itemId,
                                sectionId,
                                currentShelving,
                                onDragStartPositionY
                            );
                        })
                    );
                },
                updateItem: (id, sectionID) => {
                    const currentShelving = get().getCurrentShelving();
                    set(
                        produce((state) => {
                            state.shelving[currentShelving.name].sections = updateItem(currentShelving, id, sectionID);
                        })
                    );
                },
                removeSpiritItems: (itemId?: string) => {
                    const currentShelving = get().getCurrentShelving();

                    // Определяем, превращается ли призрачная штанга в обычную
                    let isRodBeingConverted = false;
                    if (itemId) {
                        const spiritItem = currentShelving.sections
                            .flatMap((section) => section.items)
                            .find((item) => item.id === itemId && item.isSpirit);
                        isRodBeingConverted = !!(spiritItem && spiritItem.type?.some((t) => t === "Штанга"));
                    }

                    let updatedSections = removeSpiritItems(
                        currentShelving,
                        itemId,
                        useStoreSystem.getState().sectionFilling.isFilling
                    );

                    // Если призрачная штанга стала обычной, пересчитываем ID штанг в цепочках
                    if (isRodBeingConverted) {
                        console.log("Призрачная штанга превращена в обычную, пересчитываем ID штанг в цепочках");
                        updatedSections = updateRodBitrixIds(updatedSections);
                    }

                    set(
                        produce((state) => {
                            state.shelving[currentShelving.name].sections = updatedSections;
                        })
                    );
                },
                addSpiritItems: (spiritsMap, item) => {
                    const currentShelving = get().getCurrentShelving();
                    if (["front", "left", "right", "back"].includes(currentShelving.name)) {
                        set(
                            produce((state) => {
                                let sections = addSpiritItems(spiritsMap, item, currentShelving);

                                // Обновляем штанги в частях полок после добавления новых элементов
                                const hasShelfRods = sections.some((section) =>
                                    section.items.some((item) =>
                                        item.parts.some((part) => part.type?.some((t) => t === "Штанга"))
                                    )
                                );

                                if (hasShelfRods) {
                                    sections = updateShelfRodIds(sections);
                                }

                                state.shelving[currentShelving.name].sections = sections;
                            })
                        );
                    }
                },
                deleteCurrentEditItem: () => {
                    //TODO переделать на функцию которая принимает id или еще что и удаляет объект
                    const currentShelving = get().getCurrentShelving();
                    const currentSectionId = useStoreSystem.getState().currentSectionId;
                    const currentEditItemId = useStoreSystem.getState().currentEditItem.id;

                    // Найдем удаляемый элемент для определения, является ли он полкой или штангой
                    const deletedItem = currentShelving.sections
                        .flatMap((section) => section.items)
                        .find((item) => item.id === currentEditItemId);

                    const isShelfDelete = deletedItem && deletedItem.type?.includes("Полка");
                    const isRodDelete = deletedItem && deletedItem.type?.some((t) => t === "Штанга");
                    const deletedShelfHeight = deletedItem?.position[1];

                    const newSections = currentShelving.sections.map((section) => {
                        if (section.id === currentSectionId) {
                            return {
                                ...section,
                                items: section.items.filter((item) => item.id !== currentEditItemId),
                            };
                        } else {
                            return section;
                        }
                    });

                    // Если удаляем полку, найдем и удалим роды на той же высоте только в этой секции
                    let finalSections = newSections;
                    if (isShelfDelete && deletedShelfHeight !== undefined) {
                        finalSections = newSections.map((section) => {
                            // Удаляем роды только в той же секции, где была удалена полка
                            if (section.id === currentSectionId) {
                                return {
                                    ...section,
                                    items: section.items.filter((item) => {
                                        // Удаляем роды на той же высоте, что и удаленная полка
                                        if (
                                            item.type?.includes("Штанга") &&
                                            Math.abs(item.position[1] - deletedShelfHeight) < 0.01
                                        ) {
                                            return false;
                                        }
                                        return true;
                                    }),
                                };
                            }
                            return section;
                        });
                    }

                    // Если удаляем штангу или полку со штангами, пересчитываем ID штанг
                    const hasRodsAfterDelete = finalSections.some((section) =>
                        section.items.some((item) => item.type?.some((t) => t === "Штанга"))
                    );

                    if ((isRodDelete || isShelfDelete) && hasRodsAfterDelete) {
                        console.log(
                            "Удален элемент (deleteCurrentEditItem), влияющий на штанги, пересчитываем ID штанг в цепочках"
                        );
                        finalSections = updateRodBitrixIds(finalSections);
                    }

                    set(
                        produce((state) => {
                            state.shelving[currentShelving.name].sections = finalSections;
                        })
                    );
                    useStoreSystem.getState().setCurrentEditItem(undefined, false, null);
                },
                removeItem: (itemId) => {
                    const currentShelving = get().getCurrentShelving();

                    // Найдем удаляемый элемент для определения, является ли он полкой или штангой
                    const deletedItem = currentShelving.sections
                        .flatMap((section) => section.items)
                        .find((item) => item.id === itemId);

                    const isShelfDelete = deletedItem && deletedItem.type?.includes("Полка");
                    const isRodDelete = deletedItem && deletedItem.type?.some((t) => t === "Штанга");
                    const deletedShelfHeight = deletedItem?.position[1];

                    // Найдем секцию, в которой был удаленный элемент
                    let deletedItemSectionId: string | null = null;
                    for (const section of currentShelving.sections) {
                        if (section.items.some((item) => item.id === itemId)) {
                            deletedItemSectionId = section.id;
                            break;
                        }
                    }

                    const newSections = currentShelving.sections.map((section) => {
                        return {
                            ...section,
                            items: section.items.filter((item) => item.id !== itemId),
                        };
                    });

                    // Если удаляем полку, найдем и удалим роды на той же высоте только в той же секции
                    let finalSections = newSections;
                    if (isShelfDelete && deletedShelfHeight !== undefined && deletedItemSectionId) {
                        finalSections = newSections.map((section) => {
                            // Удаляем роды только в той же секции, где была удалена полка
                            if (section.id === deletedItemSectionId) {
                                return {
                                    ...section,
                                    items: section.items.filter((item) => {
                                        // Удаляем роды на той же высоте, что и удаленная полка
                                        if (
                                            item.type?.includes("Штанга") &&
                                            Math.abs(item.position[1] - deletedShelfHeight) < 0.01
                                        ) {
                                            return false;
                                        }
                                        return true;
                                    }),
                                };
                            }
                            return section;
                        });
                    }

                    // Если удаляем штангу или полку со штангами, пересчитываем ID штанг
                    const hasRods = finalSections.some((section) =>
                        section.items.some((item) => item.type?.some((t) => t === "Штанга"))
                    );

                    if ((isRodDelete || isShelfDelete) && hasRods) {
                        console.log("Удален элемент, влияющий на штанги, пересчитываем ID штанг в цепочках");
                        finalSections = updateRodBitrixIds(finalSections);
                    }

                    set(
                        produce((state) => {
                            state.shelving[currentShelving.name].sections = finalSections;
                        })
                    );
                },
                resetShelvingStore: (withFilling, withRoom) => {
                    //TODO вот тут подумать как сбрасывать и что сбрасывать, пока в лоб чищу балки и секции
                    set(
                        produce((state: ShelvingStoreInterface) => {
                            if (withFilling) {
                                Object.values(Sides).forEach((side) => {
                                    state.shelving[side].sections = [];
                                    state.shelving[side].beams = [];
                                    state.shelving[side].connectorYPositions = {};
                                });
                            }
                            if (withRoom) {
                                Object.values(Sides).forEach((side) => {
                                    state.shelving[side] = { ...state.shelving[side], ...INITIAL_SHELVING_SIZES };
                                });
                            }

                            state.colorsItems.shelves = "white";
                            state.colorsItems.guides = "white";
                            state.colorsItems.perfpanels = "white";
                            state.colorsItems.brackets = "red";
                            state.colorsItems.tireRacks = "red";
                            state.colorsItems.workbenchToolbox = "orange";
                        })
                    );
                },
                loadProject: (project) => {
                    Object.entries(project).forEach(([stateKey, stateValue]) => {
                        set(
                            produce((state) => {
                                state[stateKey] = stateValue;
                            })
                        );
                    });
                    if (project?.shelving && isNewSite()) {
                        const projectColorsItems = {
                            ...get().colorsItems,
                            ...(project?.colorsItems || {}),
                            workbenchToolbox: project?.colorsItems?.workbenchToolbox ?? get().colorsItems.workbenchToolbox ?? "orange",
                        };
                        const { shelving } = updateItemsColors(
                            projectColorsItems.brackets,
                            project.shelving,
                            projectColorsItems.tireRacks ?? projectColorsItems.brackets
                        );
                        set({ colorsItems: projectColorsItems, shelving });
                    }
                },
            }),
            {
                version: 230423,
                name: "storeWalls",
            }
        ),
        {
            name: "shelvingStore",
            enabled: process.env.NODE_ENV === "development",
        }
    )
);
